import * as React from 'react'

import { Button } from './button'
import { cn } from '../../lib/utils'

export interface CardButtonProps
    extends Omit<React.ComponentProps<typeof Button>, 'variant'> {
    icon: React.ReactNode
    title: string
    description?: string
    variant?: 'default' | 'muted'
}

const CardButton = React.forwardRef<HTMLButtonElement, CardButtonProps>(
    (
        {
            icon,
            title,
            description,
            variant = 'default',
            className,
            children,
            disabled,
            ...props
        },
        ref
    ) => {
        return (
            <Button
                ref={ref}
                variant="outline"
                disabled={disabled}
                className={cn(
                    'group flex flex-col items-center justify-center p-6 rounded-xl border-2 h-auto min-h-0 transition-all duration-200 whitespace-normal text-center',
                    'disabled:opacity-50 disabled:cursor-wait',
                    variant === 'default'
                        ? 'border-border bg-card hover:border-accent hover:bg-accent/5'
                        : 'border-border bg-card hover:border-muted-foreground/30 hover:bg-muted/30 text-muted-foreground hover:text-muted-foreground',
                    disabled &&
                    (variant === 'default'
                        ? 'disabled:border-border disabled:bg-muted/30'
                        : 'disabled:bg-muted/20'),
                    className
                )}
                {...props}
            >
                <div
                    className={cn(
                        'w-12 h-12 rounded-full flex items-center justify-center mb-3 transition-colors',
                        variant === 'default'
                            ? 'bg-accent/10 group-hover:bg-accent/20'
                            : 'bg-muted/50 group-hover:bg-muted/70'
                    )}
                >
                    {icon}
                </div>
                <span
                    className={cn(
                        'text-base font-medium',
                        variant === 'default' ? 'text-foreground' : 'text-muted-foreground'
                    )}
                >
                    {title}
                </span>
                {description && (
                    <span className="text-xs text-muted-foreground mt-1">{description}</span>
                )}
                {children}
            </Button>
        )
    }
)
CardButton.displayName = 'CardButton'

export { CardButton }
